import './component.scss'
import '@stimulus/polyfills'
import { Application, Controller } from 'stimulus'

const app = Application.start()

app.register('refosumu-company-show-header-tabs', class extends Controller {
  static targets = ["nav", "spacer", "navItem", "link"]

  connect() {
    // Sticky機能用の状態管理
    this.stickyTop = 0
    this.isSticky = false
    this.headerHeight = 0
    
    // アクティブタブ管理
    this.currentActiveSection = '#top'
    this.observer = null

    // 各機能の初期化
    this.initHeaderTabsJump()
    this.initStickyHeaderTabs()
    this.initActiveTabSwitching()
  }

  disconnect() {
    // イベントリスナーのクリーンアップ
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.updateStickyTop)
    window.removeEventListener('scroll', this.checkTopSection)
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  // ジャンプ用オフセット（目標位置の調整）
  getJumpOffset(isSticky) {
    const isMobile = window.innerWidth < 768

    if (isMobile) {
      return isSticky ? 45 : 96 // スマホ用オフセット
    } else {
      return isSticky ? 53 : 138 // PC用オフセット
    }
  }

  // タブクリック時のスムーススクロール機能
  initHeaderTabsJump() {
    this.linkTargets.forEach(link => {
      link.addEventListener('click', (e) => {
        // 無効化されたタブの場合は何もしない
        if (link.closest('.company-show-header-tabs__nav-item--disabled')) {
          return
        }

        e.preventDefault()
        const targetId = link.getAttribute('href').substring(1)
        
        if (targetId === 'top') {
          // 会社トップの場合はページの最上部にスクロール
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        } else {
          const targetElement = document.getElementById(targetId)
          
          if (targetElement) {
            // Sticky状態に応じてオフセットを調整
            const offset = this.getJumpOffset(this.isSticky)
            const targetPosition = targetElement.offsetTop - offset

            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            })
          }
        }
      })
    })
  }

  // ヘッダータブのSticky機能初期化
  initStickyHeaderTabs() {
    // サイトヘッダーの参照を取得
    this.siteHeader = document.querySelector('.site-header')

    this.updateStickyTop()

    // メソッドのthisバインド
    this.handleScroll = this.handleScroll.bind(this)
    this.updateStickyTop = this.updateStickyTop.bind(this)

    // イベントリスナー登録
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.updateStickyTop)
  }

  // Sticky位置の更新
  updateStickyTop() {
    if (!this.isSticky) {
      this.stickyTop = this.element.offsetTop
      this.headerHeight = this.navTarget.offsetHeight
    }
  }

  // サイトヘッダーがまだ表示されているかチェック
  isSiteHeaderVisible() {
    if (!this.siteHeader) return false
    const rect = this.siteHeader.getBoundingClientRect()
    // サイトヘッダーの下端が画面上部より下にある場合は表示中
    return rect.bottom > 0
  }

  // スクロール時のSticky制御
  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    // サイトヘッダーがまだ表示されている間はstickyにしない
    if (this.isSiteHeaderVisible()) {
      if (this.isSticky) {
        // Sticky状態を解除
        this.element.classList.remove('company-show-header-tabs--sticky')
        this.spacerTarget.style.height = '0px'
        this.isSticky = false
      }
      return
    }

    if (scrollTop > this.stickyTop && !this.isSticky) {
      // Sticky状態にする
      this.element.classList.add('company-show-header-tabs--sticky')
      this.spacerTarget.style.height = this.headerHeight + 'px'
      this.isSticky = true
    } else if (scrollTop <= this.stickyTop && this.isSticky) {
      // Sticky状態を解除
      this.element.classList.remove('company-show-header-tabs--sticky')
      this.spacerTarget.style.height = '0px'
      this.isSticky = false
    }
  }

  // アクティブタブの自動切り替え機能初期化
  initActiveTabSwitching() {
    const sections = [
      { id: 'company-reputation', href: '#company-reputation' },
      { id: 'reform-examples', href: '#reform-examples' }
    ]
    
    // 初期状態は会社トップをアクティブに
    this.setActiveTab('#top')
    
    // 無効化されていないセクションのみを監視対象にフィルタリング
    const activeSections = sections.filter(section => {
      const tabElement = this.element.querySelector(`[href="${section.href}"]`)
      const navItem = tabElement ? tabElement.closest('.company-show-header-tabs__nav-item') : null
      return navItem && !navItem.classList.contains('company-show-header-tabs__nav-item--disabled')
    })
    
    // Intersection Observerでセクションの表示状態を監視
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id
          const section = activeSections.find(s => s.id === sectionId)
          if (section) {
            this.setActiveTab(section.href)
          }
        }
      })
    }, {
      rootMargin: '-55px 0px -100% 0px' // 上から55pxの位置でタブ切り替え
    })
    
    // 有効なセクションのみを監視対象に追加
    activeSections.forEach(section => {
      const element = document.getElementById(section.id)
      if (element) {
        this.observer.observe(element)
      }
    })
    
    // 会社トップへの切り替え判定用のスクロールイベント
    this.checkTopSection = this.checkTopSection.bind(this)
    window.addEventListener('scroll', this.checkTopSection)
  }

  // アクティブタブの設定
  setActiveTab(sectionHref) {
    if (this.currentActiveSection === sectionHref) return // 同じ場合は処理しない
    
    this.currentActiveSection = sectionHref
    
    // 全てのタブからアクティブクラスを削除し、対象のタブに追加
    this.navItemTargets.forEach(item => {
      const link = item.querySelector('.company-show-header-tabs__link')
      if (link) {
        const href = link.getAttribute('href')
        if (href === sectionHref) {
          item.classList.add('company-show-header-tabs__nav-item--active')
        } else {
          item.classList.remove('company-show-header-tabs__nav-item--active')
        }
      }
    })
  }

  // 会社トップセクションの判定
  checkTopSection() {
    // 有効な最初のセクションを見つける
    const sections = ['company-reputation', 'reform-examples']
    let firstActiveSection = null
    
    for (const sectionId of sections) {
      const element = document.getElementById(sectionId)
      if (element) {
        const tabElement = this.element.querySelector(`[href="#${sectionId}"]`)
        const navItem = tabElement ? tabElement.closest('.company-show-header-tabs__nav-item') : null
        
        if (navItem && !navItem.classList.contains('company-show-header-tabs__nav-item--disabled')) {
          firstActiveSection = element
          break
        }
      }
    }
    
    if (firstActiveSection) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const firstSectionTop = firstActiveSection.offsetTop - 100 // 100px手前で判定

      if (scrollTop < firstSectionTop) {
        this.setActiveTab('#top')
      }
    }
  }
})
